import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { AlertTriangle, AlertCircle, Info } from "lucide-react";
import type { ErrorSeverity } from "@/lib/error-types";

interface SeverityToggleProps {
  value: ErrorSeverity | 'all';
  onChange: (value: ErrorSeverity | 'all') => void;
  disabled?: boolean;
}

export function SeverityToggle({ value, onChange, disabled }: SeverityToggleProps) {
  return (
    <ToggleGroup
      type="single"
      value={value}
      onValueChange={(v) => {
        if (v) onChange(v as ErrorSeverity | 'all');
      }}
      className="gap-0 rounded-md border bg-muted p-0.5"
      disabled={disabled}
    >
      <ToggleGroupItem
        value="all"
        className="rounded-sm px-3 py-1.5 text-xs font-medium data-[state=on]:bg-background data-[state=on]:shadow-sm"
        aria-label="Show all"
      >
        All
      </ToggleGroupItem>
      <ToggleGroupItem
        value="error"
        className="rounded-sm px-3 py-1.5 text-xs font-medium data-[state=on]:bg-background data-[state=on]:shadow-sm data-[state=on]:text-destructive"
        aria-label="Show errors only"
      >
        <AlertCircle className="mr-1 h-3 w-3" />
        Errors
      </ToggleGroupItem>
      <ToggleGroupItem
        value="warning"
        className="rounded-sm px-3 py-1.5 text-xs font-medium data-[state=on]:bg-background data-[state=on]:shadow-sm data-[state=on]:text-yellow-600"
        aria-label="Show warnings only"
      >
        <AlertTriangle className="mr-1 h-3 w-3" />
        Warnings
      </ToggleGroupItem>
    </ToggleGroup>
  );
}

// Badge variant helper for severity
export function getSeverityBadgeClass(severity: ErrorSeverity): string {
  switch (severity) {
    case 'error':
      return 'bg-destructive text-destructive-foreground';
    case 'warning':
      return 'bg-yellow-500 text-white dark:bg-yellow-600';
    case 'info':
    default:
      return 'bg-secondary text-secondary-foreground';
  }
}
